/**
 * The kinds of car a "Cars" field picks from, one kind at a time — Acriss, Category, Acriss Group — with their labels,
 * the order of the pills and the texts about groups (CU-869fmhmx8 E6; moved here with `KindPillsField`, CU-869fmpqfm,
 * so the BI dashboards and the price analyzers say the same). Pure and alias-free: `node --test` runs it.
 */

import { kindPills } from './kindPills.js'

export const CARS = Object.freeze({ ALL: 'all', GROUP: 'group', ACRISS: 'acriss', CATEGORY: 'category' })

export const CARS_LABEL = Object.freeze({ [CARS.GROUP]: 'Acriss Group', [CARS.ACRISS]: 'Acriss', [CARS.CATEGORY]: 'Category' })
// the pills inside the menu, in this order, and the one it opens on (Curro, 2026-10-26)
export const CARS_KIND_ORDER = Object.freeze([CARS.ACRISS, CARS.CATEGORY, CARS.GROUP])
export const DEFAULT_CARS_TAB = CARS.ACRISS

export const NO_GROUPS_TEXT = 'Your company has no Acriss groups. Create them in Settings › Acriss groups.'
export const GROUPS_HEADER = 'Your company’s groups · Settings › Acriss groups'

/**
 * The pills of the menu (E6: groups for every user). Without groups the Acriss Group pill stays, says `none yet`, and
 * its list is the note that leads to Settings › Acriss groups: nothing can be picked there. `groupsCount` null =
 * still loading.
 */
export function carsKinds({ groupsCount = null } = {}) {
	return kindPills(CARS_KIND_ORDER, CARS_LABEL, { [CARS.GROUP]: groupsCount })
}

// ---------------------------------------------------------------------------
// The picked values, one kind at a time (CU-869fmhmx8 E1; moved here from the BI dashboards' `cars.js` in
// CU-869fmpqfm v1.2 so the price analyzers' Cars selector picks several values by the same rules). A pick is
// `{ cars, carsIds }`: the kind (`all` | `group` | `acriss` | `category`) and its values.
// ---------------------------------------------------------------------------

const KINDS = [CARS.GROUP, CARS.ACRISS, CARS.CATEGORY]

// the server's bounds (`invalid_cars`): codes as RESULTS stores them (`-CAR`, `iwar`), never upper-cased
export const CARS_MAX_VALUES = 100
const MAX_LENGTH = { [CARS.ACRISS]: 10, [CARS.CATEGORY]: 50 }

export const NO_CARS = Object.freeze({ cars: CARS.ALL, carsIds: [] })

const positiveInt = v => {
	const n = Number(v)
	return Number.isInteger(n) && n > 0 ? n : null
}

/** A valid `{ cars, carsIds }` (sorted, unique) or all cars. `ids` is a list or a comma-separated string. */
export function parseCars(kind, ids) {
	if (!KINDS.includes(kind)) return { ...NO_CARS }
	const raw = Array.isArray(ids) ? ids : typeof ids === 'string' && ids ? ids.split(',') : []
	let values
	if (kind === CARS.GROUP) {
		values = raw.map(positiveInt)
		if (values.some(v => v === null)) return { ...NO_CARS }
		values = [...new Set(values)].sort((a, b) => a - b)
	} else {
		if (raw.some(v => typeof v !== 'string' || !v.length || v.length > MAX_LENGTH[kind])) return { ...NO_CARS }
		values = [...new Set(raw)].sort()
	}
	if (!values.length || values.length > CARS_MAX_VALUES) return { ...NO_CARS }
	return { cars: kind, carsIds: values }
}

export const carsOn = view => !!view && view.cars !== CARS.ALL && !!view.carsIds?.length

/** Picking `ids` on the pill `tab`: replaces any other kind; nothing picked is all cars */
export function pickCars(view, tab, ids) {
	if (!ids?.length) return view.cars === tab || !carsOn(view) ? { ...NO_CARS } : { cars: view.cars, carsIds: [...view.carsIds] }
	return parseCars(tab, ids.map(v => (tab === CARS.GROUP ? Number(v) : v)))
}

/** The labels of the picked values: a group by its name (from the answer, or from the list of groups) */
export function carsLabels(view, { groups = [], options = [] } = {}) {
	if (!carsOn(view)) return []
	if (view.cars !== CARS.GROUP) return [...view.carsIds]
	const names = new Map([...options.map(o => [Number(o.value), o.label]), ...groups.map(g => [Number(g.id), g.name])])
	return view.carsIds.map(id => names.get(Number(id)) || `#${id}`)
}
